import { useRouter } from 'next/router';
import { useState, ChangeEvent } from 'react';

import { Flex, Pagination } from '@vimeo/bokeh';
import { useViewer } from '@vimeo/viewer';
import {
  ShowcasePlaylist,
  ShowcasePlaylistSkeleton,
  ShowcasePlaylistErrorView,
  ShowcasePlaylistEmptyView,
} from '@vimeo/viewer-showcase-ui/components/ShowcasePlaylist';

import { useGetPaginatedVideos } from '../hooks/use-get-playlist-videos';
import { useGetShowcase } from '../hooks/use-get-showcase';
import { useSelectedId } from '../hooks/use-selected-id';
import { useAlbumStore } from '../state/albumStore';
import { bpSelectToggleAutoplay, bpShowcaseVideoClick } from '../utils/analytics';
import { DownloadListModule } from './DownloadListModule';

interface ClipPlaylistModuleProps {
  playerContainerRef: React.RefObject<HTMLDivElement>;
  bannerHeight?: string;
  isNewCustomizeControls: boolean;
}

export const ClipPlaylistModule = ({
  playerContainerRef,
  bannerHeight,
  isNewCustomizeControls,
}: ClipPlaylistModuleProps) => {
  const router = useRouter();
  const { selectedId, selectedClipData } = useSelectedId();
  const viewer = useViewer();

  const { setContinuousPlay, isShowcaseEmbedded } = useAlbumStore((state) => ({
    isShowcaseEmbedded: state.isShowcaseEmbedded,
    setContinuousPlay: state.setContinuousPlay,
  }));
  const [activeDownloadClipId, setActiveDownloadClipId] = useState<string | null>(null);

  const {
    allowContinuousPlay,
    allowDownloads,
    gridSize,
    showGridProfileImage,
    showGridProfileName,
    showGridVideoTitle,
    showGridVideoCard,
    showVideoCount,
    user,
  } = useGetShowcase();
  const { clips, total, isLoading, error, mutate, setPage, perPage, page } =
    useGetPaginatedVideos();

  const onAutoplayChange = (checked: boolean) => {
    setContinuousPlay(checked);
  };

  const onAutoplayClick = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setContinuousPlay(checked);
    bpSelectToggleAutoplay(
      selectedId ?? '',
      checked,
      viewer,
      selectedClipData?.privacy?.view || '',
    );
  };

  const onVideoCardClick = (
    link: string,
    clipId: string,
    videoPrivacy: string,
    videoOwnerUri: string,
  ) => {
    bpShowcaseVideoClick(user?.uri || '', clipId, videoPrivacy, videoOwnerUri, viewer);
    router.push(link);
    if (playerContainerRef.current && isShowcaseEmbedded && bannerHeight) {
      playerContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const onDownloadModalClose = () => {
    setActiveDownloadClipId(null);
  };

  const onDownloadClick = (clipId: string) => {
    setActiveDownloadClipId(clipId);
  };

  if (isLoading || !clips) {
    return (
      <ShowcasePlaylistSkeleton
        gridSize={gridSize}
        total={total}
        isAutoplayDefaultChecked={allowContinuousPlay}
        onAutoplayChange={onAutoplayChange}
        showVideoPlayerCards={showGridVideoCard}
        isNewCustomizeControls={isNewCustomizeControls}
      />
    );
  }

  if (error) {
    return <ShowcasePlaylistErrorView onRetry={() => mutate()} />;
  }

  if (clips.length === 0) {
    return <ShowcasePlaylistEmptyView />;
  }

  const paginationRequired = page && perPage && total > perPage;

  return (
    <>
      <ShowcasePlaylist
        showVideoCount={showVideoCount}
        clips={clips}
        activeClipId={selectedId}
        gridSize={gridSize}
        total={total}
        isAutoplayDefaultChecked={allowContinuousPlay}
        onVideoCardClick={onVideoCardClick}
        onAutoplayChange={onAutoplayChange}
        onAutoplayClick={onAutoplayClick}
        allowDownloads={allowDownloads}
        onDownloadClick={onDownloadClick}
        showVideoPlayerCards={showGridVideoCard}
        showProfileImage={showGridProfileImage}
        showProfileName={showGridProfileName}
        showVideoTitle={showGridVideoTitle}
        isNewCustomizeControls={isNewCustomizeControls}
      />
      {paginationRequired ? (
        <Flex justifyContent="center">
          <Pagination
            count={total}
            page={page}
            onPageChange={(details) => setPage(details.page)}
            pageSize={perPage}
            color="text-primary"
          />
        </Flex>
      ) : null}
      {allowDownloads ? (
        <DownloadListModule
          isDownloadModalOpen={!!activeDownloadClipId}
          onClose={onDownloadModalClose}
          clipId={activeDownloadClipId}
        />
      ) : null}
    </>
  );
};
